import {
  FolderIcon,
  MoreVerticalIcon,
  PencilIcon,
  PlusIcon,
  PowerIcon,
} from 'lucide-react'
import { useState } from 'react'
import { Boton } from '@/components/ui/Boton'
import {
  MenuContenido,
  MenuDesplegable,
  MenuDisparador,
  MenuItem,
} from '@/components/ui/MenuDesplegable'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { cn } from '@/lib/cn'
import type { ClienteAdmin, ProyectoAdmin } from '@/lib/schemas/admin'
import { useActivarProyecto } from './api'
import { Confirmacion } from './Confirmacion'
import { formatearPeriodo } from './formato'
import { ModalProyecto } from './ModalProyecto'
import { Panel, VacioPanel } from './Panel'

interface ColumnaProyectosProps {
  cliente: ClienteAdmin | null
  proyectos: ProyectoAdmin[]
  cargando: boolean
  seleccionado: string | null
  alElegir: (id: string) => void
  busqueda: string
  mostrarInactivos: boolean
  abrirNuevo: boolean
  alCerrarNuevo: () => void
}

/** P03 · columna "Proyectos de <cliente>" (§8.6). */
export function ColumnaProyectos({
  cliente,
  proyectos,
  cargando,
  seleccionado,
  alElegir,
  busqueda,
  mostrarInactivos,
  abrirNuevo,
  alCerrarNuevo,
}: ColumnaProyectosProps) {
  const [editando, setEditando] = useState<ProyectoAdmin | null>(null)
  const [nuevoLocal, setNuevoLocal] = useState(false)
  const [aDesactivar, setADesactivar] = useState<ProyectoAdmin | null>(null)
  const activar = useActivarProyecto()
  const avisar = useAvisar()

  const filtro = busqueda.trim().toLowerCase()
  const visibles = proyectos.filter(
    (p) =>
      (mostrarInactivos || p.activo) &&
      (!filtro || p.nombre.toLowerCase().includes(filtro)),
  )

  const modalAbierto = abrirNuevo || nuevoLocal || editando !== null
  const cerrarModal = () => {
    setNuevoLocal(false)
    setEditando(null)
    alCerrarNuevo()
  }

  const botonNuevo = (tamano: 'chico' | 'normal') => (
    <Boton
      tamano={tamano}
      disabled={!cliente}
      onClick={() => {
        setNuevoLocal(true)
      }}
    >
      <PlusIcon className="size-4" aria-hidden />
      Nuevo proyecto
    </Boton>
  )

  return (
    <Panel
      titulo={cliente ? `Proyectos de ${cliente.nombre}` : 'Proyectos'}
      accion={botonNuevo('chico')}
    >
      {!cliente ? (
        <p className="px-4 py-6 text-sm text-tinta-suave">Elegí un cliente.</p>
      ) : cargando ? (
        <p className="px-4 py-6 text-sm text-tinta-suave">Cargando…</p>
      ) : proyectos.length === 0 ? (
        <VacioPanel
          icono={<FolderIcon className="size-8" aria-hidden />}
          texto="Este cliente no tiene proyectos."
        >
          {botonNuevo('normal')}
        </VacioPanel>
      ) : visibles.length === 0 ? (
        <p className="px-4 py-6 text-sm text-tinta-suave">
          {filtro
            ? 'Ningún proyecto coincide con la búsqueda.'
            : 'Sólo hay proyectos inactivos. Activá "Mostrar inactivos".'}
        </p>
      ) : (
        <ul className="flex flex-col gap-1 p-2" aria-label="Proyectos">
          {visibles.map((p) => {
            const activo = p.id === seleccionado
            return (
              <li key={p.id} className="flex items-stretch gap-1">
                <button
                  type="button"
                  onClick={() => {
                    alElegir(p.id)
                  }}
                  aria-current={activo ? 'true' : undefined}
                  data-proyecto={p.nombre}
                  className={cn(
                    'flex min-h-16 min-w-0 flex-1 flex-col justify-center rounded-2xl px-3 text-left',
                    'hover:bg-lienzo focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tinta',
                    activo && 'bg-lienzo ring-1 ring-borde',
                    !p.activo && 'opacity-60',
                  )}
                >
                  <span className="truncate text-sm font-medium">{p.nombre}</span>
                  <span className="truncate text-xs text-tinta-suave">
                    {p.activo
                      ? formatearPeriodo(p.periodoDesde, p.periodoHasta)
                      : 'Inactivo'}
                  </span>
                </button>
                <MenuDesplegable>
                  <MenuDisparador asChild>
                    <Boton
                      variante="sutil"
                      tamano="icono"
                      aria-label={`Opciones de ${p.nombre}`}
                      className="self-center"
                    >
                      <MoreVerticalIcon className="size-5" aria-hidden />
                    </Boton>
                  </MenuDisparador>
                  <MenuContenido>
                    <MenuItem
                      onSelect={() => {
                        setEditando(p)
                      }}
                    >
                      <PencilIcon className="size-4" aria-hidden />
                      Editar
                    </MenuItem>
                    {p.activo ? (
                      <MenuItem
                        peligro
                        onSelect={() => {
                          setADesactivar(p)
                        }}
                      >
                        <PowerIcon className="size-4" aria-hidden />
                        Desactivar
                      </MenuItem>
                    ) : (
                      <MenuItem
                        onSelect={() => {
                          void activar
                            .mutateAsync({ id: p.id, activo: true })
                            .then(() => {
                              avisar(`${p.nombre} reactivado`)
                            })
                        }}
                      >
                        <PowerIcon className="size-4" aria-hidden />
                        Reactivar
                      </MenuItem>
                    )}
                  </MenuContenido>
                </MenuDesplegable>
              </li>
            )
          })}
        </ul>
      )}

      {cliente && (
        <ModalProyecto
          abierto={modalAbierto}
          alCerrar={cerrarModal}
          clienteId={cliente.id}
          clienteNombre={cliente.nombre}
          proyecto={editando}
          otros={proyectos.filter((p) => p.activo)}
          alGuardar={(p) => {
            if (!editando) alElegir(p.id)
          }}
        />
      )}

      <Confirmacion
        abierto={aDesactivar !== null}
        alCerrar={() => {
          setADesactivar(null)
        }}
        titulo="Desactivar proyecto"
        texto={`${aDesactivar?.nombre ?? ''} sale del selector y deja de estar disponible para las personas con acceso. Se conserva todo.`}
        confirmar="Desactivar"
        peligro
        alConfirmar={async () => {
          if (!aDesactivar) return
          await activar.mutateAsync({ id: aDesactivar.id, activo: false })
          avisar(`${aDesactivar.nombre} desactivado`)
        }}
      />
    </Panel>
  )
}
